import { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import cn from "../../../utils/cn";
import { chunk, cloneDeep, concat, noop, some } from "lodash";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { RETURN_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const getDays = (year, month) => {
  const firstDay = moment({
    year,
    month
  }).startOf("month").startOf("week");
  const lastDay = moment({
    year,
    month
  }).endOf("month").endOf("week");
  let days = [];
  let day = firstDay;
  do {
    days = concat(days, cloneDeep(day));
    day = day.add(1, "day");
  } while (day.isBefore(lastDay));
  return chunk(days, 7);
};
export const isDayInArray = function (day, daysArray) {
  let isUtc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return some(daysArray, selected => {
    if (isUtc) {
      return day.utc().isSame(moment(selected), "day");
    }
    return day.isSame(moment(selected), "day");
  });
};
export const isDaySelected = (day, selectedDays, dateRange, isUtc) => {
  if (dateRange.start || dateRange.end) {
    return isDayInArray(day, [dateRange.start, dateRange.end], isUtc);
  }
  return isDayInArray(day, selectedDays, isUtc);
};
export const isDayDisabled = (day, disabledDays, shouldDisableDay) => isDayInArray(day, disabledDays) || shouldDisableDay(day);
export const isDayInRange = (day, dateRange) => {
  if (dateRange.start && dateRange.end) {
    return day.isBetween(moment(dateRange.start), moment(dateRange.end), "day");
  }
  return false;
};
export const isDayOutOfMonth = (day, month) => day.month() !== month;
const Calendar = _ref => {
  let {
    weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    year,
    month,
    selectedDays = [],
    onDayClick = noop,
    disabledDays = [],
    className = "",
    id = "",
    shouldDisableDay = noop,
    dateRange = {
      start: null,
      end: null
    },
    isUtc = true
  } = _ref;
  const days = getDays(year, month);
  const calendarRef = useRef(null);
  const announcerRef = useRef(null); // Ref for the announcer

  const calendarClass = cn({
    "border-separate text-text-blue text-center select-none text-sm": true
  }, className);
  const dayClass = day => {
    const isSelected = isDaySelected(day, selectedDays, dateRange, isUtc);
    const isDisabled = isDayDisabled(day, disabledDays, shouldDisableDay);
    const isOutOfRange = isDayOutOfMonth(day, month);
    const isInRange = isDayInRange(day, dateRange) && !isDayOutOfMonth(day, month);
    const baseClasses = "justify-center items-center rounded overflow-hidden w-8 h-8";
    const disabledClasses = isDisabled ? "cursor-default text-dark-border-grey" : "";
    const selectedClasses = isSelected ? "bg-action-blue text-white" : "";
    const outOfMonthClasses = isOutOfRange ? "text-medium-grey" : "";
    const inRangeClasses = isInRange ? "bg-dark-fill-blue" : "";
    let hoverClasses = "";
    if (isSelected) {
      hoverClasses = "";
    } else if (!isDisabled && !isInRange) {
      hoverClasses = "hover:bg-medium-fill-grey cursor-pointer";
    } else {
      hoverClasses = "cursor-none focus:bg-light-border-blue focus:outline-none";
    }
    return cn(baseClasses, selectedClasses, disabledClasses, outOfMonthClasses, inRangeClasses, hoverClasses);
  };
  const handleFocus = day => {
    const isSelected = isDaySelected(day, selectedDays, dateRange, isUtc);
    if (announcerRef.current) {
      announcerRef.current.textContent = isSelected ? "Day ".concat(day.format("DD"), " of ").concat(day.format("MMMM YYYY"), " is selected.") : "Day ".concat(day.format("DD"), " of ").concat(day.format("MMMM YYYY"), ".");
    }
  };
  useEffect(() => {
    const handleKeyDown = e => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
      }
      const selectedDayIndex = days.flat().findIndex(day => isDaySelected(day, selectedDays, dateRange, isUtc));
      if (selectedDayIndex === -1) return;
      const numCols = days[0].length;
      let nextIndex;
      switch (e.key) {
        case "ArrowUp":
          nextIndex = selectedDayIndex - numCols;
          break;
        case "ArrowDown":
          nextIndex = selectedDayIndex + numCols;
          break;
        case "ArrowLeft":
          nextIndex = selectedDayIndex - 1;
          break;
        case "ArrowRight":
          nextIndex = selectedDayIndex + 1;
          break;
        default:
          return;
      }
      const nextDay = days[Math.floor(nextIndex / numCols)][nextIndex % numCols];
      if (!isDayDisabled(nextDay, disabledDays, shouldDisableDay)) {
        onDayClick(nextDay);
        handleFocus(nextDay);
      }
    };
    const calendarElement = calendarRef.current;
    if (calendarElement) {
      calendarElement.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      if (calendarElement) {
        calendarElement.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [selectedDays, dateRange, isUtc, days, disabledDays, onDayClick, shouldDisableDay]);
  return /*#__PURE__*/_jsxs("div", {
    ref: calendarRef,
    className: calendarClass,
    id: id,
    children: [/*#__PURE__*/_jsx("div", {
      "aria-live": "polite",
      ref: announcerRef,
      className: "sr-only"
    }), /*#__PURE__*/_jsxs("table", {
      style: {
        borderCollapse: "separate",
        borderSpacing: "2px"
      },
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsx("tr", {
          children: weekDays.map(day => /*#__PURE__*/_jsx("th", {
            className: "font-normal",
            children: day
          }, "week-day-".concat(day)))
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: days.map(week => /*#__PURE__*/_jsx("tr", {
          className: "h-full w-full leading-5 rounded",
          children: week.map(day => /*#__PURE__*/_jsx("td", {
            className: dayClass(day),
            children: /*#__PURE__*/_jsx("div", {
              role: "button",
              onClick: () => isDayDisabled(day, disabledDays, shouldDisableDay) ? noop() : onDayClick(day),
              onFocus: () => handleFocus(day),
              tabIndex: isDayDisabled(day, disabledDays, shouldDisableDay) ? -1 : 0,
              "aria-label": "Day ".concat(day.format("DD"), " of ").concat(day.format("MMMM YYYY")),
              onKeyDown: handleKeyDown => isKeyPressed(handleKeyDown, "Enter", RETURN_KEY_CODE) && onDayClick(day),
              children: day.date()
            })
          }, "day-".concat(day.dayOfYear())))
        }, "week-".concat(week[0].week())))
      })]
    })]
  });
};
Calendar.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** Override the inline-styles of the elements || **Not Recommended** */
  /** Current month to display */
  month: PropTypes.number.isRequired,
  /** Current year to display */
  year: PropTypes.number.isRequired,
  /** List of week days label */
  weekDays: PropTypes.array,
  /** Day click callback. Passes the moment Object */
  onDayClick: PropTypes.func,
  /** List of selected days. Can be a moment object or anything moment can parse */
  selectedDays: PropTypes.array,
  /** List of disabled days. Can be a moment object or anything moment can parse */
  disabledDays: PropTypes.array,
  /** Function returning a boolean */
  shouldDisableDay: PropTypes.func,
  /** Start and end date. This overrides the selectedDays prop */
  dateRange: PropTypes.shape({
    start: PropTypes.any,
    end: PropTypes.any
  }),
  /** are the dates in UTC format? */
  isUtc: PropTypes.bool
};
export default Calendar;
//# sourceMappingURL=Calendar.js.map